import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const HIDDEN = { x: 0, y: 0, width: 0, height: 0, opacity: 0 };
const MAX_HEIGHT = 56;
const TARGET_CLASS = "sidebar-pill-target";

const nearlyEqual = (a, b) => Math.abs(a - b) < 0.5;

const samePill = (a, b) =>
  nearlyEqual(a.x, b.x) &&
  nearlyEqual(a.y, b.y) &&
  nearlyEqual(a.width, b.width) &&
  nearlyEqual(a.height, b.height) &&
  a.opacity === b.opacity;

const measureElement = (container, element) => {
  if (!container || !element) return null;

  const c = container.getBoundingClientRect();
  const r = element.getBoundingClientRect();

  if (r.width < 2 || r.height < 2) return null;
  if (r.height > MAX_HEIGHT * 1.5) return null;

  return {
    x: r.left - c.left + container.scrollLeft,
    y: r.top - c.top + container.scrollTop,
    width: r.width,
    height: Math.min(r.height, MAX_HEIGHT),
    opacity: 1,
  };
};

const setTargetClass = (container, element) => {
  if (!container) return;
  container.querySelectorAll(`.${TARGET_CLASS}`).forEach((node) => {
    if (node !== element) node.classList.remove(TARGET_CLASS);
  });
  if (element) element.classList.add(TARGET_CLASS);
};

export const useSidebarActivePill = (containerRef, deps = []) => {
  const [pill, setPill] = useState(HIDDEN);
  const pillRef = useRef(HIDDEN);
  const pendingHrefRef = useRef(null);
  const lastGoodMeasureAtRef = useRef(0);
  const rafRef = useRef(0);
  const timersRef = useRef([]);
  const resizeObsRef = useRef(null);
  const scheduleRef = useRef(() => {});

  const commit = useCallback((next) => {
    if (!next) return;
    setPill((prev) => {
      if (samePill(prev, next)) return prev;
      pillRef.current = next;
      return next;
    });
  }, []);

  const placeOn = useCallback(
    (element) => {
      const container = containerRef.current;
      const next = measureElement(container, element);
      if (!next) return false;
      setTargetClass(container, element);
      lastGoodMeasureAtRef.current = Date.now();
      commit(next);
      return true;
    },
    [commit, containerRef],
  );

  const applyMeasure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (pendingHrefRef.current) {
      const pending = container.querySelector(
        `a.link[href="${pendingHrefRef.current}"]`,
      );
      if (pending && placeOn(pending)) {
        if (pending.classList.contains("active")) {
          pendingHrefRef.current = null;
        }
        return;
      }
    }

    const active = container.querySelector("a.link.active");
    // Closed accordion/section must not leave selection chrome on the next header
    if (active?.closest("[hidden]")) {
      pendingHrefRef.current = null;
      setTargetClass(container, null);
      commit(HIDDEN);
      return;
    }
    if (active && placeOn(active)) {
      pendingHrefRef.current = null;
      return;
    }

    if (
      pillRef.current.opacity === 1 &&
      Date.now() - lastGoodMeasureAtRef.current < 300
    ) {
      return;
    }
    setTargetClass(container, null);
    commit(HIDDEN);
  }, [commit, containerRef, placeOn]);

  const scheduleMeasure = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        applyMeasure();
      });
    });
  }, [applyMeasure]);

  useEffect(() => {
    scheduleRef.current = scheduleMeasure;
  }, [scheduleMeasure]);

  const snapToElement = useCallback(
    (element) => {
      if (!(element instanceof Element)) return;
      pendingHrefRef.current = element.getAttribute("href");
      placeOn(element);
      scheduleMeasure();
    },
    [placeOn, scheduleMeasure],
  );

  const clearIntent = useCallback(() => {
    pendingHrefRef.current = null;
  }, []);

  const refreshPill = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [0, 40, 120].map((ms) =>
      setTimeout(() => scheduleRef.current(), ms),
    );
    applyMeasure();
  }, [applyMeasure]);

  useLayoutEffect(() => {
    pendingHrefRef.current = null;
    applyMeasure();
    scheduleMeasure();
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const onScroll = () => scheduleRef.current();
    const onResize = () => refreshPill();

    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => scheduleRef.current())
        : null;
    ro?.observe(container);
    resizeObsRef.current = ro;

    const mo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => scheduleRef.current())
        : null;
    mo?.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    applyMeasure();

    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
      mo?.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      timersRef.current.forEach(clearTimeout);
      resizeObsRef.current?.disconnect();
      setTargetClass(container, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { pill, refreshPill, snapToElement, clearIntent };
};
